/**
 * devBypassService.ts
 *
 * STAGING-ONLY escape hatch for when Microsoft SSO is misconfigured
 * or the operator just needs to dogfood the app without a real
 * Microsoft account.
 *
 * DO NOT enable on production. The renderer in LoginPage hides the
 * button unless one of these is true:
 *   • Vite MODE === 'development' (npm run dev)
 *   • hostname matches a known staging host
 *
 * What it does:
 *   1. Sign in anonymously via Firebase Auth (creates a stable
 *      anon UID per browser, persisted in IndexedDB).
 *   2. Upsert /users/{uid} with role='superadmin' + isActive=true
 *      so the firestore.rules isHr() / isLeader() helpers grant
 *      access.
 *   3. Caller navigates to the dashboard.
 *
 * Requires Firebase Auth → Sign-in providers → Anonymous to be
 * enabled in the project console. If it's not, the signInAnonymously
 * call throws auth/operation-not-allowed; surface that to the user.
 *
 * Per ENGINEERING.md §0: this is a SYMPTOM fix (operator can't log
 * in today) AND a class guardrail (staging needs an escape hatch so
 * future SSO outages don't block dogfooding). On production it must
 * always 403.
 */

import { signInAnonymously } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

/**
 * Hosts where the dev bypass is allowed to render in the UI.
 *
 * Per user feedback "ini tidak perlu" — bypass button is no longer
 * exposed on any DEPLOYED host (staging or production). It only
 * renders during `npm run dev` locally. If you ever need to re-expose
 * it on staging (e.g. SSO outage), add the hostname back here.
 */
const STAGING_HOSTS = new Set<string>([
  'localhost',
  '127.0.0.1',
]);

/** True when the bypass button should render. Used by LoginPage. */
export function isDevBypassAllowed(): boolean {
  // Vite dev mode (npm run dev) always counts.
  if (import.meta.env.MODE === 'development') return true;
  // Hostname allowlist — localhost only by default. All deployed hosts
  // (epr-service-staging.web.app fallback, epr-staging.rascalrepublic.com,
  // epr.rascalrepublic.com prod) intentionally NEVER see the button —
  // bypass is for `npm run dev` only.
  if (typeof window === 'undefined') return false;
  return STAGING_HOSTS.has(window.location.hostname);
}

/**
 * Sign in anonymously and grant superadmin via /users/{uid}.
 * Resolves with the assumed uid; caller redirects to /dashboard.
 *
 * Throws on:
 *   • auth/operation-not-allowed → enable Anonymous provider in console
 *   • permission-denied → /users rule too tight; loosen for staging
 *   • non-staging host → defensive runtime guard (UI also hides button)
 */
export async function signInDevBypass(): Promise<string> {
  if (!isDevBypassAllowed()) {
    throw new Error(
      'Dev bypass disabled on this host. Use Microsoft SSO instead.',
    );
  }
  const credential = await signInAnonymously(auth);
  const uid = credential.user.uid;

  // Upsert the users doc so isHr() returns true. Only writes the
  // role/isActive fields on first run; subsequent calls reuse the
  // existing doc (idempotent — supports repeated dev refreshes).
  const userRef = doc(db, 'users', uid);
  const existing = await getDoc(userRef);
  if (!existing.exists()) {
    await setDoc(userRef, {
      uid,
      name:      'Dev User (staging bypass)',
      email:     `dev-bypass-${uid.slice(0, 6)}@staging.local`,
      role:      'superadmin',
      isActive:  true,
      isLeader:  true,
      appRoles:  { epr: 'superadmin' },
      createdAt: serverTimestamp(),
      // Tag so we can audit / clean up later.
      _devBypass: true,
    });
  }
  return uid;
}
